// contexts/AuthContext.jsx
import React, { createContext, useEffect, useState, useRef } from "react";
import { supabase } from "../supabaseClient";
import { Bugfender } from "@bugfender/sdk";
import { useAnalytics } from "../analytics/AnalyticsContext"; // import our analytics hook

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatStyle, setChatStyle] = useState("short");
  const [allowedDomains, setAllowedDomains] = useState([
    "techcrunch.com",
    "theverge.com",
    "wired.com",
    "apnews.com",
    "bbc.com",
  ]);
  const [personalInterest, setPersonalInterest] = useState(""); // New state for personal_interest
  const [profileLoaded, setProfileLoaded] = useState(false);

  const profileFetchedRef = useRef(false);

  const { identifyUser } = useAnalytics();

  // Handle auth state.
  useEffect(() => {
    let mounted = true;
    async function loadSession() {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
      // We set profileLoaded false only initially.
      setProfileLoaded(false);
    }
    loadSession();
  
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (mounted) {
        // Check if the user really changed
        const newUserId = session?.user?.id;
        const currentUserId = user?.id;
        // Only reset profileLoaded if the user ID has changed.
        if ((event === "SIGNED_IN" || event === "SIGNED_OUT") && newUserId !== currentUserId) {
          profileFetchedRef.current = false;
          setProfileLoaded(false);
          // console.log("Auth event resetting profileLoaded:", event);
        }
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    });
  
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [user?.id]);

  // Fetch profile when user changes.
  useEffect(() => {
    let mounted = true;
    async function fetchProfile() {
      if (!user || profileFetchedRef.current) return;
      // Fetch chat_style, allowed_domains, and personal_interest
      const { data: profileData, error } = await supabase
        .from("profiles")
        .select("chat_style, allowed_domains, personal_interest")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!mounted) return;
      if (!error && profileData) {
        setChatStyle(profileData.chat_style || "insights");
        if (profileData.allowed_domains) {
          // console.log("Allowed domains:", profileData.allowed_domains);
          setAllowedDomains(profileData.allowed_domains);
        }
        if (profileData.personal_interest) {
          setPersonalInterest(profileData.personal_interest);
        }
      } else {
        // console.log("No profile found, using default settings");
      }
      profileFetchedRef.current = true;
      setProfileLoaded(true);
    }
    fetchProfile();
    // console.log("User changed, fetching profile", user);
    return () => {
      mounted = false;
    };
  }, [user?.id]);

  const updateChatStyle = async (newStyle) => {
    if (!user) return;
    setChatStyle(newStyle);
    const { error } = await supabase
      .from("profiles")
      .upsert(
        { user_id: user.id, chat_style: newStyle },
        { onConflict: "user_id" }
      );
    if (error) {
      console.error("Error updating chat style:", error);
    }
  };

  const updateAllowedDomains = async (newDomains) => {
    if (!user) return;
    setAllowedDomains(newDomains);
    const { error } = await supabase
      .from("profiles")
      .upsert(
        { user_id: user.id, allowed_domains: newDomains },
        { onConflict: "user_id" }
      );
    if (error) {
      console.error("Error updating allowed domains:", error);
    }
  };

  const value = {
    user,
    session,
    loading,
    chatStyle,
    allowedDomains,
    personalInterest, // expose personalInterest in the context
    profileLoaded,
    updateChatStyle,
    updateAllowedDomains,
  };

  // Simple helper to generate a unique identifier (for device and session IDs)
  function generateUniqueId() {
    return "_" + Math.random().toString(36).substr(2, 9);
  }

    // When a user is available (i.e. logged in), call our abstraction to identify the user.
    useEffect(() => {
      if (user) {
        // Use the abstraction layer's identifyUser function
        identifyUser(user.id);
        // console.log("User identified:", user.id);
      }
    }, [user, identifyUser]);

  // Set Bugfender device keys when the user is available.
  useEffect(() => {
    if (user) {
      // Set the user email key in Bugfender.
      Bugfender.setDeviceKey("user_email", user.email);

      // Check if a device ID already exists in localStorage; otherwise, generate one.
      let deviceId = localStorage.getItem("deviceId");
      if (!deviceId) {
        deviceId = generateUniqueId();
        localStorage.setItem("deviceId", deviceId);
      }
      Bugfender.setDeviceKey("device_id", deviceId);
    }
  }, [user?.email]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
